import React, { useEffect, useMemo, useRef, useState } from 'react';

export default function MerchantCenterPerformance() {
  const [merchantId, setMerchantId] = useState('');
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [history, setHistory] = useState([]); // daily rollup time series
  const [pageIndex, setPageIndex] = useState(1); // 1-based (client-side)
  const PAGE_SIZE = 25;
  const [hasMoreServerPages, setHasMoreServerPages] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  // Hierarchical category selection: array of selected parts ['Shoes', 'Sneakers']
  const [selectedPath, setSelectedPath] = useState([]);
  // Brand filter (single select). '' = all brands.
  const [selectedBrand, setSelectedBrand] = useState('');
  const DEMO_BRAND_IDS_MC = new Set(['2b08baa8-dd8e-4b5c-93cd-dd38b9b3718a', 'ec9873c2-0edb-44eb-ac6f-2670c8e56dc4']);
  // Map of productId (product_view.id) -> benchmark info from Merchant Center (live)
  const [benchmarkByProductId, setBenchmarkByProductId] = useState({});
  const [benchmarkLoading, setBenchmarkLoading] = useState(false);
  // Benchmark UX additions (2026-05-13): filter / sort / stats / manual resync
  const [onlyWithBenchmark, setOnlyWithBenchmark] = useState(false);
  const [sortBy, setSortBy] = useState('default'); // default | has_benchmark | delta_pct_asc | delta_pct_desc
  const [benchmarkStats, setBenchmarkStats] = useState(null); // { total_products, with_benchmark }
  const [benchSyncing, setBenchSyncing] = useState(false);
  const [benchSyncMsg, setBenchSyncMsg] = useState('');

  useEffect(() => { setActiveImageIdx(0); }, [selectedProduct]);

  const brandId = useMemo(() => {
    try {
      const current = localStorage.getItem('currentBrandId') || sessionStorage.getItem('currentBrandId');
      if (current) return current;
    } catch (_) {}
    const tryKeys = ['brandId', 'selectedBrandId', 'activeBrandId', 'brand_id'];
    for (const k of tryKeys) {
      try { const v = localStorage.getItem(k) || sessionStorage.getItem(k); if (v) return v; } catch (_) {}
    }
    const tryJson = ['selectedBrand', 'currentBrand', 'activeBrand'];
    for (const k of tryJson) {
      try {
        const raw = localStorage.getItem(k) || sessionStorage.getItem(k);
        if (raw) { const obj = JSON.parse(raw); if (obj?.id) return obj.id; }
      } catch(_) {}
    }
    try {
      const m = window.location.pathname.match(/[0-9a-fA-F-]{36}/);
      if (m) return m[0];
    } catch(_) {}
    return '';
  }, []);

  // Rollup for active products. Prefer DB history last point; fallback to live status structure if present.
  const activeRollup = useMemo(() => {
    if (Array.isArray(history) && history.length > 0) {
      const last = history[history.length - 1];
      return {
        active: Number(last.active || 0),
        pending: Number(last.pending || 0),
        disapproved: Number(last.disapproved || 0),
        expiring: Number(last.expiring || 0),
        destination: last.destination || '',
        country: last.country || ''
      };
    }
    const productsArr = status?.status?.products || [];
    const preferred = ['SurfacesAcrossGoogle', 'Shopping', 'DisplayAds'];
    const picked = preferred.map(d => productsArr.find(p => p?.destination === d)).find(Boolean) || productsArr[0];
    if (!picked) return null;
    const st = picked.statistics || {};
    return {
      active: Number(st.active || 0),
      pending: Number(st.pending || 0),
      disapproved: Number(st.disapproved || 0),
      expiring: Number(st.expiring || 0),
      destination: picked.destination,
      country: picked.country
    };
  }, [status, history]);


  const fmtDate = (raw) => {
    if (!raw) return '';
    const s = String(raw);
    if (s.length >= 10) return s.slice(0, 10);
    return s;
  };

  const MiniLineChart = ({ data }) => {
    const safeData = (Array.isArray(data) ? data : []).map(d => ({ ...d, date: fmtDate(d.date) }));
    const width = 920;
    const height = 200;
    const padding = { t: 10, r: 20, b: 24, l: 36 };
    const innerW = width - padding.l - padding.r;
    const innerH = height - padding.t - padding.b;
    const containerRef = useRef(null);
    const [hoverI, setHoverI] = useState(null);
    const xs = safeData.map(d => d.date);
    const series = ['active', 'pending', 'disapproved', 'expiring'];
    const colors = { active: '#16a34a', pending: '#6b7280', disapproved: '#ef4444', expiring: '#f59e0b' };
    const maxY = Math.max(1, ...safeData.flatMap(d => series.map(s => Number(d[s] || 0))));
    const xPos = (i) => padding.l + (safeData.length <= 1 ? innerW : (i * innerW) / (safeData.length - 1));
    const yPos = (v) => padding.t + innerH - (innerH * v) / maxY;
    const toPath = (key) => {
      return safeData.map((d, i) => `${i === 0 ? 'M' : 'L'} ${xPos(i)} ${yPos(Number(d[key] || 0))}`).join(' ');
    };
    const toAreaPath = (key) => {
      if (!safeData.length) return '';
      const topPath = safeData.map((d, i) => `${i === 0 ? 'M' : 'L'} ${xPos(i)} ${yPos(Number(d[key] || 0))}`).join(' ');
      const lastX = xPos(safeData.length - 1);
      const firstX = xPos(0);
      const baseY = padding.t + innerH;
      return `${topPath} L ${lastX} ${baseY} L ${firstX} ${baseY} Z`;
    };
    const yTicks = 4;
    const yTickVals = Array.from({ length: yTicks + 1 }, (_, i) => Math.round((i * maxY) / yTicks));
    const handleMove = (evt) => {
      if (!safeData.length) return;
      const rect = evt.currentTarget.getBoundingClientRect();
      if (!rect.width) return;
      // SVG width="100%" + viewBox="0 0 920 200" → ekrandaki px, viewBox birimine ölçeklenmeli.
      // Aksi halde fare X'i (px) doğrudan viewBox birimiyle hesaplanır ve sağa gittikçe kayar.
      const scale = width / rect.width; // viewBox birimi / CSS piksel
      const relX = (evt.clientX - rect.left) * scale - padding.l;
      const step = safeData.length <= 1 ? innerW : innerW / (safeData.length - 1);
      const idx = Math.max(0, Math.min(safeData.length - 1, Math.round(relX / step)));
      setHoverI(idx);
    };
    const handleLeave = () => setHoverI(null);
    const tooltip = hoverI != null ? safeData[hoverI] : null;
    return (
      <div ref={containerRef} className="bg-white rounded-2xl border shadow-sm p-5 mb-6 relative">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-medium text-gray-800">Product Status Trend</div>
          <div className="flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-2 rounded bg-[#16a34a]" /> Approved</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-2 rounded bg-[#f59e0b]" /> Limited</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-2 rounded bg-[#ef4444]" /> Not approved</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-2 rounded bg-[#6b7280]" /> Under review</span>
          </div>
        </div>
        {safeData.length < 3 && safeData.length > 0 && (
          <div className="text-xs text-gray-400 mb-2 flex items-center gap-1.5">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
            Historical data is being collected daily. The trend chart will become more detailed over the coming days.
          </div>
        )}
        <svg width="100%" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Product status line chart">
          {/* Y grid */}
          {yTickVals.map((v, i) => (
            <g key={i}>
              <line x1={padding.l} x2={padding.l + innerW} y1={yPos(v)} y2={yPos(v)} stroke="#E5E7EB" strokeWidth="1" />
              <text x={padding.l - 6} y={yPos(v)} textAnchor="end" dominantBaseline="middle" fontSize="10" fill="#6B7280">{v.toLocaleString('tr-TR')}</text>
            </g>
          ))}
          {/* X labels */}
          {safeData.map((d, i) => (
            <text key={i} x={xPos(i)} y={height - 6} textAnchor="middle" fontSize="10" fill="#6B7280">{d.date.slice(5)}</text>
          ))}
          {/* Active area fill for clarity */}
          {safeData.length > 0 && (
            <path d={toAreaPath('active')} fill="#16a34a1f" stroke="none" />
          )}
          {/* Lines */}
          {series.map(key => (
            <path key={key} d={toPath(key)} fill="none" stroke={colors[key]} strokeWidth="2.2" />
          ))}
          {/* Hover capture */}
          <rect x="0" y="0" width={width} height={height} fill="transparent" onMouseMove={handleMove} onMouseLeave={handleLeave} />
          {/* Hover marker */}
          {hoverI != null && (
            <g>
              <line x1={xPos(hoverI)} x2={xPos(hoverI)} y1={padding.t} y2={padding.t + innerH} stroke="#9CA3AF" strokeDasharray="3,3" />
              {series.map(key => (
                <circle key={key} cx={xPos(hoverI)} cy={yPos(Number(safeData[hoverI][key] || 0))} r="3" fill={colors[key]} />
              ))}
            </g>
          )}
        </svg>
        {/* Tooltip */}
        {tooltip && (
          <div
            className="absolute bg-white border border-gray-200 rounded-md shadow-lg px-3 py-2 text-xs pointer-events-none"
            style={{
              // Marker viewBox biriminde çiziliyor; tooltip HTML → konteyner genişliğine oransal
              // yerleştir (px değil %) ki SVG ölçeğinden bağımsız marker'ın üstünde dursun.
              left: `${Math.min(Math.max((xPos(hoverI) / width) * 100, 8), 92)}%`,
              transform: 'translateX(-50%)',
              top: 8
            }}
          >
            <div className="font-medium text-gray-800 mb-1">{tooltip.date}</div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
              <div className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm" style={{ backgroundColor: colors.active }} /> Active</div>
              <div className="text-right text-gray-800">{Number(tooltip.active || 0).toLocaleString('tr-TR')}</div>
              <div className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm" style={{ backgroundColor: colors.pending }} /> Pending</div>
              <div className="text-right text-gray-800">{Number(tooltip.pending || 0).toLocaleString('tr-TR')}</div>
              <div className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm" style={{ backgroundColor: colors.disapproved }} /> Disapproved</div>
              <div className="text-right text-gray-800">{Number(tooltip.disapproved || 0).toLocaleString('tr-TR')}</div>
              <div className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm" style={{ backgroundColor: colors.expiring }} /> Expiring</div>
              <div className="text-right text-gray-800">{Number(tooltip.expiring || 0).toLocaleString('tr-TR')}</div>
            </div>
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    const boot = async () => {
      try {
        setLoading(true);
        // Load merchantId from connections if not stored
        let mid = '';
        if (brandId) {
          // DEMO: inject synthetic data for the specific brand and short-circuit
          if (DEMO_BRAND_IDS_MC.has(brandId)) {
            const now = new Date();
            const months = Array.from({ length: 6 }, (_, i) => {
              const d = new Date(now);
              d.setDate(1);
              d.setMonth(d.getMonth() - (5 - i));
              return d.toISOString().slice(0, 10);
            });
            const demoHistory = months.map((dt, idx) => ({
              date: dt,
              active: 2400 + idx * 120,
              pending: Math.max(15, 95 - idx * 12),
              disapproved: Math.max(8, 35 - idx * 5),
              expiring: 25 + idx * 5,
              destination: 'Shopping',
              country: 'TR',
            }));
            
            // Generic placeholder images
            const imgPool = [
              'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=400&h=400&fit=crop',
              'https://images.unsplash.com/photo-1549298916-b41d501d3772?w=400&h=400&fit=crop',
              'https://images.unsplash.com/photo-1595950653106-6c9ebd614d3a?w=400&h=400&fit=crop',
              'https://images.unsplash.com/photo-1560769629-975ec94e6a86?w=400&h=400&fit=crop',
              'https://images.unsplash.com/photo-1600185365926-3a2ce3cdb9eb?w=400&h=400&fit=crop',
              'https://images.unsplash.com/photo-1608231387042-66d1773070a5?w=400&h=400&fit=crop',
              'https://images.unsplash.com/photo-1606107557195-0e29a4b5b4aa?w=400&h=400&fit=crop',
              'https://images.unsplash.com/photo-1587563871167-1ee9c731aefb?w=400&h=400&fit=crop',
            ];
            
            const productData = [
              { name: 'Klasik Deri Sneaker - Beyaz', brand: 'Premium Collection', cat: 'Ayakkabı > Sneaker > Günlük', price: 1899, benchmark: 1750 },
              { name: 'Koşu Ayakkabısı Pro Max', brand: 'Sport Line', cat: 'Ayakkabı > Spor > Koşu', price: 2499, benchmark: 2650 },
              { name: 'Casual Oxford Erkek', brand: 'Classic Wear', cat: 'Ayakkabı > Klasik > Oxford', price: 1599, benchmark: 1520 },
              { name: 'Yürüyüş Botu Outdoor', brand: 'Adventure Gear', cat: 'Ayakkabı > Bot > Outdoor', price: 3299, benchmark: 3450 },
              { name: 'Slip-On Loafer Kahverengi', brand: 'Comfort Plus', cat: 'Ayakkabı > Loafer > Günlük', price: 1299, benchmark: 1180 },
              { name: 'Basketbol Ayakkabısı High Top', brand: 'Sport Line', cat: 'Ayakkabı > Spor > Basketbol', price: 2899, benchmark: 2750 },
              { name: 'Kadın Spor Ayakkabı Pembe', brand: 'Active Style', cat: 'Ayakkabı > Kadın > Spor', price: 1799, benchmark: 1850 },
              { name: 'Çocuk Okul Ayakkabısı', brand: 'Kids Collection', cat: 'Ayakkabı > Çocuk > Okul', price: 899, benchmark: 920 },
              { name: 'Yazlık Sandalet Erkek', brand: 'Summer Vibes', cat: 'Ayakkabı > Sandalet > Erkek', price: 699, benchmark: 650 },
              { name: 'Platform Sneaker Kadın', brand: 'Trend Fashion', cat: 'Ayakkabı > Kadın > Platform', price: 1999, benchmark: 2100 },
              { name: 'Deri Bot Kışlık Siyah', brand: 'Winter Collection', cat: 'Ayakkabı > Bot > Kışlık', price: 2799, benchmark: 2650 },
              { name: 'Fitness Ayakkabısı Unisex', brand: 'Gym Master', cat: 'Ayakkabı > Spor > Fitness', price: 1699, benchmark: 1800 },
              { name: 'Trekking Ayakkabısı Pro', brand: 'Adventure Gear', cat: 'Ayakkabı > Outdoor > Trekking', price: 2999, benchmark: 3150 },
              { name: 'Casual Canvas Lacivert', brand: 'Urban Style', cat: 'Ayakkabı > Canvas > Günlük', price: 599, benchmark: 550 },
              { name: 'Topuklu Stiletto Siyah', brand: 'Elegance', cat: 'Ayakkabı > Kadın > Topuklu', price: 1499, benchmark: 1380 },
            ];
            
            const demoProducts = productData.map((item, i) => ({
              offerId: `SKU-PRD-${1000 + i}`,
              productId: `online:tr:TR:SKU-PRD-${1000 + i}`,
              title: item.name,
              brand: item.brand,
              availability: (i % 8 === 7) ? 'out of stock' : 'in stock',
              price: { value: item.price, currency: 'TRY' },
              salePrice: (i % 4 === 0) ? { value: Math.round(item.price * 0.85), currency: 'TRY' } : null,
              imageLink: imgPool[i % imgPool.length],
              additionalImages: [imgPool[(i + 1) % imgPool.length], imgPool[(i + 2) % imgPool.length]],
              category: item.cat,
              link: 'https://example.com/product/' + (1000 + i),
              contentLanguage: 'tr',
              targetCountry: 'TR',
              condition: 'new',
              gtin: `8690000${1000 + i}`,
              customLabel0: 'Demo',
              customLabel1: item.brand,
              customLabel2: 'AW24',
              customLabel3: i % 2 === 0 ? 'Erkek' : 'Kadın',
              customLabel4: ['Günlük', 'Spor', 'Klasik', 'Outdoor'][i % 4],
            }));
            
            // Build benchmark data for demo products
            const demoBenchmarks = {};
            productData.forEach((item, i) => {
              const productId = `online:tr:TR:SKU-PRD-${1000 + i}`;
              demoBenchmarks[productId] = {
                benchmarkPrice: item.benchmark,
                benchmarkCurrency: 'TRY',
                deltaPct: ((item.price - item.benchmark) / item.benchmark * 100).toFixed(1)
              };
            });
            
            setMerchantId('DEMO-MC-TR');
            setStatus({
              status: {
                products: [
                  { destination: 'Shopping', statistics: { active: 2850, pending: 45, disapproved: 12, expiring: 38 } },
                  { destination: 'SurfacesAcrossGoogle', statistics: { active: 2920, pending: 38, disapproved: 10, expiring: 32 } },
                ],
              },
            });
            setHistory(demoHistory);
            setAllProducts(demoProducts);
            setBenchmarkByProductId(demoBenchmarks);
            setLoading(false);
            return;
          }
          const resp = await fetch('https://api.glowytics.com/api/auth/connections', {
            headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
            credentials: 'include',
          });
          if (resp.ok) {
            const data = await resp.json();
            let found = null;
            if (Array.isArray(data)) {
              found = data.find(x => (x?.platform === 'MERCHANT_CENTER') || (x?.platform === 'merchant_center')) || null;
            } else if (data && typeof data === 'object') {
              const mc = data['MERCHANT_CENTER'] || data['merchant_center'] || null;
              if (mc) found = mc;
            }
            mid = found?.customerId || found?.merchantId || '';
          }
        }
        if (mid) setMerchantId(String(mid));

        // Fetch account status history (clean time series from DB, grouped by date)
        if (mid && brandId) {
          const st = await fetch(`https://api.glowytics.com/api/google-ads/merchant-center/accountstatus/history?merchantId=${encodeURIComponent(mid)}&days=90`, {
            headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
            credentials: 'include',
          });
          const stText = await st.text(); let stJson; try { stJson = JSON.parse(stText); } catch { stJson = { raw: stText }; }
          if (!st.ok) throw new Error(stJson?.error || st.statusText || 'Failed to load account status');
          const series = Array.isArray(stJson?.data) ? stJson.data : [];
          setHistory(series);
        }

        // Fetch initial page of products (preview) from DB for client-side pagination.
        // We now load products incrementally in PAGE_SIZE chunks instead of loading everything at once.
        if (mid && brandId) {
          const limit = PAGE_SIZE;
          const offset = 0;
          const pr = await fetch(
            `https://api.glowytics.com/api/google-ads/merchant-center/products/preview/db?merchantId=${encodeURIComponent(
              mid
            )}&limit=${limit}&offset=${offset}`,
            {
              headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
              credentials: 'include'
            }
          );
          const prText = await pr.text();
          let prJson;
          try {
            prJson = JSON.parse(prText);
          } catch {
            prJson = { raw: prText };
          }
          if (!pr.ok) throw new Error(prJson?.error || pr.statusText || 'Failed to load products');
          const rows = Array.isArray(prJson?.data) ? prJson.data : [];
          const mapped = rows.map((r) => ({
            offerId: r.offer_id,
            productId: r.product_id,
            title: r.title,
            brand: r.brand,
            availability: r.availability,
            price: { value: r.price_value, currency: r.price_currency },
            salePrice: r.sale_price_value
              ? { value: r.sale_price_value, currency: r.sale_price_currency }
              : null,
            imageLink: r.image_link,
            additionalImages: Array.isArray(r.additional_image_links)
              ? r.additional_image_links
              : [],
            category: r.google_product_category,
            link: r.link,
            contentLanguage: r.content_language,
            targetCountry: r.target_country,
            condition: r.condition,
            gtin: r.gtin,
            customLabel0: r.custom_label0,
            customLabel1: r.custom_label1,
            customLabel2: r.custom_label2,
            customLabel3: r.custom_label3,
            customLabel4: r.custom_label4,
            // benchmark data joined from google_mc_price_competitiveness_raw
            benchmarkPrice: r.benchmark_price,
            benchmarkCurrency: r.benchmark_currency,
            deltaPct: r.delta_pct,
            hasBenchmark: !!r.has_benchmark,
            benchmarkFetchDate: r.benchmark_fetch_date
          }));
          setAllProducts(mapped);
          setPageIndex(1);
          setHasMoreServerPages(rows.length === limit);
        }
      } catch (e) {
        setErrorMsg(e?.message || 'Load error');
      } finally {
        setLoading(false);
      }
    };
    boot();
  }, [brandId]);

  const Card = ({ label, value, sub, icon }) => (
    <div className="bg-white rounded-2xl border shadow-sm p-5">
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">{label}</div>
        {icon ? <div className="text-gray-400">{icon}</div> : null}
      </div>
      <div className="mt-2 text-2xl font-semibold text-gray-900">{value}</div>
      {sub ? <div className="mt-1 text-xs text-gray-500">{sub}</div> : null}
    </div>
  );

  // Helper to get options for a specific depth level based on current selection
  const getLevelOptions = (depth) => {
      const options = new Set();
      allProducts.forEach(p => {
          // Treat empty/null category as "Unknown Category"
          const rawCat = p.category || 'Unknown Category';
          const parts = rawCat.split('>').map(s => s.trim());
          
          // To show options at 'depth', product must match selectedPath up to that depth
          if (parts.length <= depth) return;
          
          let match = true;
          for (let i = 0; i < depth; i++) {
              if (parts[i] !== selectedPath[i]) {
                  match = false;
                  break;
              }
          }
          if (match) {
              options.add(parts[depth]);
          }
      });
      return Array.from(options).sort();
  };

  // Render dropdowns for each level
  const renderCategoryFilters = () => {
      const dropdowns = [];
      // Allow up to 6 levels deep
      for (let depth = 0; depth < 6; depth++) {
          const options = getLevelOptions(depth);
          if (options.length === 0) break; // No subcategories available at this level
          
          // If depth > 0 and previous level not selected, stop
          if (depth > 0 && !selectedPath[depth - 1]) break;

          const val = selectedPath[depth] || '';
          dropdowns.push(
              <select
                  key={depth}
                  value={val}
                  onChange={(e) => {
                      const newVal = e.target.value;
                      if (!newVal) {
                          // Reset path at this depth
                          setSelectedPath(prev => prev.slice(0, depth));
                      } else {
                          // Set value and cut deeper
                          const next = selectedPath.slice(0, depth);
                          next.push(newVal);
                          setSelectedPath(next);
                      }
                  }}
                  className="px-3 py-1.5 text-xs border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 max-w-[180px]"
              >
                  <option value="">{depth === 0 ? 'All Categories' : 'Select sub...'}</option>
                  {options.map(o => (
                      <option key={o} value={o}>{o}</option>
                  ))}
              </select>
          );
          
          // If nothing selected at this level, don't show next
          if (!val) break;
      }
      return dropdowns;
  };

  // Debounce search before triggering server-side search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch((search || '').trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Server-side search: reload products from DB endpoint with q=... so search runs across all products (not only loaded ones)
  useEffect(() => {
    const run = async () => {
      try {
        if (!brandId || !merchantId) return;
        if (DEMO_BRAND_IDS_MC.has(brandId)) return;

        setIsLoadingMore(true);
        setErrorMsg('');
        setPageIndex(1);
        setHasMoreServerPages(true);

        const qs = new URLSearchParams();
        qs.set('merchantId', merchantId);
        // When searching, fetch up to 5000 results so search covers all products
        // When not searching, use PAGE_SIZE for incremental loading
        const searchLimit = debouncedSearch ? 5000 : PAGE_SIZE;
        qs.set('limit', String(searchLimit));
        qs.set('offset', '0');
        if (debouncedSearch) qs.set('q', debouncedSearch);
        if (onlyWithBenchmark) qs.set('hasBenchmark', 'true');
        if (sortBy !== 'default') qs.set('sortBy', sortBy);

        const pr = await fetch(`https://api.glowytics.com/api/google-ads/merchant-center/products/preview/db?${qs.toString()}`, {
          headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
          credentials: 'include'
        });
        const prText = await pr.text();
        let prJson;
        try { prJson = JSON.parse(prText); } catch { prJson = { raw: prText }; }
        if (!pr.ok) throw new Error(prJson?.error || pr.statusText || 'Failed to load products');
        const rows = Array.isArray(prJson?.data) ? prJson.data : [];
        if (prJson?.stats) setBenchmarkStats(prJson.stats);
        const mapped = rows.map((r) => ({
          offerId: r.offer_id,
          productId: r.product_id,
          title: r.title,
          brand: r.brand,
          availability: r.availability,
          price: { value: r.price_value, currency: r.price_currency },
          salePrice: r.sale_price_value ? { value: r.sale_price_value, currency: r.sale_price_currency } : null,
          imageLink: r.image_link,
          additionalImages: Array.isArray(r.additional_image_links) ? r.additional_image_links : [],
          category: r.google_product_category,
          link: r.link,
          contentLanguage: r.content_language,
          targetCountry: r.target_country,
          condition: r.condition,
          gtin: r.gtin,
          customLabel0: r.custom_label0,
          customLabel1: r.custom_label1,
          customLabel2: r.custom_label2,
          customLabel3: r.custom_label3,
          customLabel4: r.custom_label4
        }));
        setAllProducts(mapped);
        setHasMoreServerPages(!debouncedSearch && rows.length === PAGE_SIZE);
      } catch (e) {
        setErrorMsg(e?.message || 'Search error');
      } finally {
        setIsLoadingMore(false);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, merchantId, brandId, onlyWithBenchmark, sortBy]);

  const filteredProducts = useMemo(() => {
    let arr = allProducts;
    // Use debouncedSearch so filtering matches the server-side query
    const q = (debouncedSearch || '').trim().toLowerCase();
    if (q) {
      arr = arr.filter(p =>
        (p.title || '').toLowerCase().includes(q) ||
        (p.offerId || '').toLowerCase().includes(q) ||
        (p.productId || '').toLowerCase().includes(q)
      );
    }
    // Filter by hierarchical path
    if (selectedPath.length > 0) {
        arr = arr.filter(p => {
            const cat = p.category || 'Unknown Category';
            const parts = cat.split('>').map(s => s.trim());
            if (parts.length < selectedPath.length) return false;
            // Check all selected parts match
            return selectedPath.every((val, i) => parts[i] === val);
        });
    }
    // Filter by brand
    if (selectedBrand) {
      arr = arr.filter(p => (p.brand || '(no brand)') === selectedBrand);
    }
    return arr;
  }, [allProducts, debouncedSearch, selectedPath, selectedBrand]);

  // Distinct brand list, sorted alphabetically; products without brand grouped under '(no brand)'
  const brandOptions = useMemo(() => {
    const set = new Set();
    for (const p of allProducts) {
      set.add((p.brand || '').trim() || '(no brand)');
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'tr'));
  }, [allProducts]);

  const visibleProducts = useMemo(() => {
    const start = (pageIndex - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return filteredProducts.slice(start, end);
  }, [filteredProducts, pageIndex]);

  const fmtMoney = (val, currency) => {
    if (val == null || !Number.isFinite(Number(val))) return '-';
    const n = Number(val);
    const cur = currency || '';
    return `${n.toLocaleString('tr-TR', { maximumFractionDigits: 2 })} ${cur}`.trim();
  };

  // NOTE (2026-05-13): Live benchmark fetch on visible rows has been REMOVED.
  // Benchmark prices now come from google_mc_price_competitiveness_raw cache, joined
  // in the products/preview/db endpoint. User can manually re-sync via "Refresh
  // benchmarks" button (top toolbar), which calls /api/auth/merchant-center/sync/price-competitiveness.
  // The benchmarkByProductId state is kept for backward compatibility only — it stays empty.

  // There can be a "next page" if the current page isn't full OR if the server
  // still has more pages we haven't fetched yet.
  const canNext = (pageIndex * PAGE_SIZE) < filteredProducts.length || hasMoreServerPages;

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <img src="/merchantcenterlogo.svg" alt="Merchant Center" className="h-10" />
        </div>

        {/* Status line chart */}
        <MiniLineChart data={(history.length ? history : (activeRollup ? [{
          date: new Date().toISOString().slice(0,10),
          active: activeRollup.active, pending: activeRollup.pending, disapproved: activeRollup.disapproved, expiring: activeRollup.expiring
        }] : []))} />

        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card
            label="Active Products"
            value={activeRollup ? activeRollup.active.toLocaleString('tr-TR') : (loading ? '...' : '0')}
          />
          <Card
            label="Pending"
            value={activeRollup ? activeRollup.pending.toLocaleString('tr-TR') : (loading ? '...' : '0')}
          />
          <Card
            label="Disapproved"
            value={activeRollup ? activeRollup.disapproved.toLocaleString('tr-TR') : (loading ? '...' : '0')}
          />
          <Card
            label="Expiring"
            value={activeRollup ? activeRollup.expiring.toLocaleString('tr-TR') : (loading ? '...' : '0')}
          />
        </div>

        {/* Products table */}
        <div className="bg-white rounded-2xl border shadow-sm p-5">
          <div className="flex flex-col gap-3 mb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="text-sm font-medium text-gray-800">Products</div>
                {/* Category Filter (Hierarchical) - Moved Left */}
                <div className="flex items-center gap-2 flex-wrap">
                  {renderCategoryFilters()}
                  {selectedPath.length > 0 && (
                    <button
                      onClick={() => setSelectedPath([])}
                      className="text-xs text-red-600 hover:text-red-800 font-medium px-2"
                    >
                      Clear
                    </button>
                  )}
                  {/* Brand filter */}
                  <select
                    value={selectedBrand}
                    onChange={(e) => setSelectedBrand(e.target.value)}
                    className="px-3 py-1.5 text-xs border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 max-w-[180px]"
                    title="Filter by brand"
                  >
                    <option value="">All Brands</option>
                    {brandOptions.map(b => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                  {selectedBrand && (
                    <button
                      onClick={() => setSelectedBrand('')}
                      className="text-xs text-red-600 hover:text-red-800 font-medium px-2"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-3 flex-wrap">
                {/* Benchmark badge + filter toggle */}
                {benchmarkStats && (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="px-2 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg font-medium">
                      Benchmark: {benchmarkStats.with_benchmark.toLocaleString('tr-TR')} / {benchmarkStats.total_products.toLocaleString('tr-TR')}
                      {benchmarkStats.total_products > 0 && (
                        <span className="ml-1 text-emerald-600">
                          ({Math.round((benchmarkStats.with_benchmark / benchmarkStats.total_products) * 100)}%)
                        </span>
                      )}
                    </span>
                    <label className="inline-flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={onlyWithBenchmark}
                        onChange={(e) => { setOnlyWithBenchmark(e.target.checked); setPageIndex(1); }}
                        className="h-3.5 w-3.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="text-gray-700">Only with benchmark</span>
                    </label>
                  </div>
                )}
                <select
                  value={sortBy}
                  onChange={(e) => { setSortBy(e.target.value); setPageIndex(1); }}
                  className="px-2 py-1.5 text-xs border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
                  title="Sort"
                >
                  <option value="default">Sort: Newest</option>
                  <option value="has_benchmark">Sort: Benchmark first</option>
                  <option value="delta_pct_desc">Sort: Δ% (most expensive)</option>
                  <option value="delta_pct_asc">Sort: Δ% (cheapest)</option>
                  <option value="benchmark_price_desc">Sort: Benchmark high→low</option>
                </select>
                <button
                  disabled={benchSyncing}
                  onClick={async () => {
                    if (!brandId) return;
                    try {
                      setBenchSyncing(true);
                      setBenchSyncMsg('');
                      const r = await fetch('https://api.glowytics.com/api/auth/merchant-center/sync/price-competitiveness', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
                        credentials: 'include',
                        body: JSON.stringify({ country: 'TR' })
                      });
                      const j = await r.json();
                      if (!r.ok) throw new Error(j?.error || r.statusText);
                      setBenchSyncMsg(`Refreshed: ${j.upserted} rows`);
                      // re-trigger reload
                      setSortBy(s => s);
                    } catch (e) {
                      setBenchSyncMsg(`Error: ${e.message}`);
                    } finally {
                      setBenchSyncing(false);
                      setTimeout(() => setBenchSyncMsg(''), 5000);
                    }
                  }}
                  className="px-2 py-1.5 text-xs border rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50"
                  title="Re-fetch benchmark prices from Google Merchant Center"
                >
                  {benchSyncing ? 'Syncing…' : 'Refresh benchmarks'}
                </button>
                {benchSyncMsg && <span className="text-[11px] text-gray-500">{benchSyncMsg}</span>}
                {/* Pagination */}
                <div className="flex items-center gap-1">
                  <button
                    className="px-2 py-1.5 text-xs border rounded-lg disabled:opacity-50"
                    disabled={pageIndex <= 1}
                    onClick={() => setPageIndex(p => Math.max(1, p - 1))}
                  >
                    Prev
                  </button>
                  <div className="text-xs text-gray-600 px-2">Page {pageIndex}</div>
                  <button
                    className="px-2 py-1.5 text-xs border rounded-lg disabled:opacity-50"
                    disabled={!canNext || isLoadingMore}
                    onClick={async () => {
                      // If user is moving beyond the already-fetched products and
                      // the server still has more pages, load the next chunk first.
                      const nextPage = pageIndex + 1;
                      const needCount = nextPage * PAGE_SIZE;
                      if (needCount > filteredProducts.length && hasMoreServerPages && !isLoadingMore) {
                        try {
                          setIsLoadingMore(true);
                          const offset = allProducts.length;
                          const limit = PAGE_SIZE;
                          const qs = new URLSearchParams();
                          qs.set('merchantId', merchantId);
                          qs.set('limit', String(limit));
                          qs.set('offset', String(offset));
                          if (debouncedSearch) qs.set('q', debouncedSearch);
                          if (onlyWithBenchmark) qs.set('hasBenchmark', 'true');
                          if (sortBy !== 'default') qs.set('sortBy', sortBy);
                          const pr = await fetch(`https://api.glowytics.com/api/google-ads/merchant-center/products/preview/db?${qs.toString()}`, {
                              headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
                              credentials: 'include'
                          });
                          const prText = await pr.text();
                          let prJson;
                          try {
                            prJson = JSON.parse(prText);
                          } catch {
                            prJson = { raw: prText };
                          }
                          if (!pr.ok) throw new Error(prJson?.error || pr.statusText || 'Failed to load products');
                          const rows = Array.isArray(prJson?.data) ? prJson.data : [];
                          const mapped = rows.map((r) => ({
                            offerId: r.offer_id,
                            productId: r.product_id,
                            title: r.title,
                            brand: r.brand,
                            availability: r.availability,
                            price: { value: r.price_value, currency: r.price_currency },
                            salePrice: r.sale_price_value
                              ? { value: r.sale_price_value, currency: r.sale_price_currency }
                              : null,
                            imageLink: r.image_link,
                            additionalImages: Array.isArray(r.additional_image_links)
                              ? r.additional_image_links
                              : [],
                            category: r.google_product_category,
                            link: r.link,
                            contentLanguage: r.content_language,
                            targetCountry: r.target_country,
                            condition: r.condition,
                            gtin: r.gtin,
                            customLabel0: r.custom_label0,
                            customLabel1: r.custom_label1,
                            customLabel2: r.custom_label2,
                            customLabel3: r.custom_label3,
                            customLabel4: r.custom_label4
                          }));
                          setAllProducts(prev => [...prev, ...mapped]);
                          if (rows.length < limit) {
                            setHasMoreServerPages(false);
                          }
                        } catch (e) {
                          console.error('Failed to load next page of products', e);
                          setHasMoreServerPages(false);
                        } finally {
                          setIsLoadingMore(false);
                        }
                      }

                      setPageIndex(p => p + 1);
                    }}
                  >
                    {isLoadingMore ? 'Loading…' : 'Next'}
                  </button>
                </div>
                {/* Search */}
                <div className="relative">
                  <input
                    value={search}
                    onChange={(e)=>{ setSearch(e.target.value); }}
                    placeholder="Search by title or ID…"
                    className="px-3 pr-3 py-1.5 text-xs border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
                  />
                </div>
                <div className="text-xs text-gray-500">{filteredProducts.length} item(s)</div>
              </div>
            </div>
          </div>
          {errorMsg ? <div className="text-sm text-red-600 mb-3">{errorMsg}</div> : null}
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-3 py-2 text-left">image</th>
                  <th className="px-3 py-2 text-left">offer id</th>
                  <th className="px-3 py-2 text-left">title</th>
                  <th className="px-3 py-2 text-left">category</th>
                  <th className="px-3 py-2 text-left">brand</th>
                  <th className="px-3 py-2 text-left">availability</th>
                  <th className="px-3 py-2 text-left">price</th>
                  <th className="px-3 py-2 text-left">benchmark</th>
                  <th className="px-3 py-2 text-left">delta</th>
                </tr>
              </thead>
              <tbody>
                {visibleProducts.length ? visibleProducts.map((p, i) => {
                  const priceStr = p.price?.value ? `${Number(p.price.value).toLocaleString('tr-TR')} ${p.price.currency || ''}` : '';
                  const saleStr = p.salePrice?.value ? `${Number(p.salePrice.value).toLocaleString('tr-TR')} ${p.salePrice.currency || ''}` : '';
                  // Prefer DB-cached benchmark (joined in backend); fall back to live API map for backwards compat
                  const liveBench = benchmarkByProductId[String(p.productId || '').trim()] || null;
                  const benchPrice = p.benchmarkPrice != null ? Number(p.benchmarkPrice) : (liveBench?.benchmarkPrice != null ? Number(liveBench.benchmarkPrice) : null);
                  const benchCurrency = p.benchmarkCurrency || liveBench?.benchmarkCurrency || p.price?.currency;
                  const benchStr = benchPrice != null ? fmtMoney(benchPrice, benchCurrency) : (benchmarkLoading ? '…' : '-');
                  // Compare against displayed price (sale if present, otherwise regular)
                  const displayPrice = p.salePrice?.value != null ? Number(p.salePrice.value) : (p.price?.value != null ? Number(p.price.value) : null);
                  const deltaPct = p.deltaPct != null
                    ? Number(p.deltaPct)
                    : (benchPrice != null && displayPrice != null && benchPrice !== 0
                        ? ((displayPrice - benchPrice) / benchPrice) * 100
                        : null);
                  return (
                    <tr key={p.offerId || p.productId || i} className="border-t hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedProduct(p)}>
                      <td className="px-3 py-2">
                        {p.imageLink ? <img src={p.imageLink} alt={p.title} className="w-12 h-12 rounded object-cover border" /> : <div className="w-12 h-12 rounded bg-gray-100 border" />}
                      </td>
                      <td className="px-3 py-2">{p.offerId || '-'}</td>
                      <td className="px-3 py-2 max-w-[360px] truncate" title={p.title}>{p.title}</td>
                      <td className="px-3 py-2 max-w-[260px] truncate" title={p.category}>{p.category || '-'}</td>
                      <td className="px-3 py-2">{p.brand}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] ${p.availability === 'in stock' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-50 text-gray-700 border border-gray-200'}`}>
                          {p.availability || '-'}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        {saleStr ? (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 line-through">{priceStr}</span>
                            <span className="text-rose-600 font-medium">{saleStr}</span>
                          </div>
                        ) : (
                          <span className="text-gray-800">{priceStr}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-800">{benchStr}</td>
                      <td className="px-3 py-2">
                        {deltaPct == null ? (
                          <span className="text-gray-400">-</span>
                        ) : (
                          <span className={`px-2 py-0.5 rounded-full text-[10px] border ${
                            deltaPct > 0
                              ? 'bg-rose-50 text-rose-700 border-rose-200'
                              : deltaPct < 0
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                : 'bg-gray-50 text-gray-700 border-gray-200'
                          }`}>
                            {deltaPct > 0 ? '+' : ''}{deltaPct.toFixed(1)}%
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                }) : (
                  <tr><td className="px-3 py-3 text-gray-500" colSpan={5}>{loading ? 'Loading…' : 'No data'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        {/* Product modal */}
        {selectedProduct && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setSelectedProduct(null)}>
            <div className="bg-white rounded-2xl shadow-2xl border w-full max-w-4xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="flex flex-col md:flex-row">
                <div className="md:w-1/2 p-5 border-b md:border-b-0 md:border-r">
                  {(() => {
                    const imgs = [selectedProduct.imageLink, ...(selectedProduct.additionalImages || [])].filter(Boolean);
                    const main = imgs[activeImageIdx] || imgs[0];
                    return (
                      <>
                        <div className="aspect-square w-full rounded-xl overflow-hidden border bg-gray-50">
                          {main ? (
                            <img src={main} alt={selectedProduct.title} className="w-full h-full object-contain" />
                          ) : null}
                        </div>
                        {imgs.length > 1 && (
                          <div className="mt-3 grid grid-cols-5 gap-2">
                            {imgs.slice(0, 5).map((u, idx) => (
                              <img
                                key={idx}
                                src={u}
                                alt=""
                                onClick={() => setActiveImageIdx(idx)}
                                className={`h-14 w-full object-cover rounded border cursor-pointer ${idx === activeImageIdx ? 'ring-2 ring-blue-500' : ''}`}
                              />
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
                <div className="md:w-1/2 p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{selectedProduct.title}</h3>
                      <div className="text-sm text-gray-500">{selectedProduct.brand || '-'}</div>
                    </div>
                    <button className="text-gray-400 hover:text-gray-600" onClick={() => setSelectedProduct(null)}>✕</button>
                  </div>
                  <div className="mt-3 flex items-center gap-3">
                    {selectedProduct.salePrice?.value ? (
                      <>
                        <span className="text-gray-400 line-through text-sm">{Number(selectedProduct.price?.value || 0).toLocaleString('tr-TR')} {selectedProduct.price?.currency || ''}</span>
                        <span className="text-rose-600 text-xl font-semibold">{Number(selectedProduct.salePrice.value).toLocaleString('tr-TR')} {selectedProduct.salePrice.currency || ''}</span>
                      </>
                    ) : (
                      <span className="text-xl font-semibold text-gray-900">{Number(selectedProduct.price?.value || 0).toLocaleString('tr-TR')} {selectedProduct.price?.currency || ''}</span>
                    )}
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] ${selectedProduct.availability === 'in stock' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-50 text-gray-700 border border-gray-200'}`}>
                      {selectedProduct.availability || '-'}
                    </span>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                    <div className="text-gray-500">Offer ID</div><div className="text-gray-800">{selectedProduct.offerId || '-'}</div>
                    <div className="text-gray-500">Product ID</div><div className="text-gray-800">{selectedProduct.productId || '-'}</div>
                    <div className="text-gray-500">GTIN</div><div className="text-gray-800">{selectedProduct.gtin || '-'}</div>
                    <div className="text-gray-500">Condition</div><div className="text-gray-800">{selectedProduct.condition || '-'}</div>
                    <div className="text-gray-500">Language</div><div className="text-gray-800">{selectedProduct.contentLanguage || '-'}</div>
                    <div className="text-gray-500">Country</div><div className="text-gray-800">{selectedProduct.targetCountry || '-'}</div>
                    <div className="col-span-2 h-px bg-gray-100 my-1"></div>
                    <div className="text-gray-500">Custom label 0</div><div className="text-gray-800">{selectedProduct.customLabel0 || '-'}</div>
                    <div className="text-gray-500">Custom label 1</div><div className="text-gray-800">{selectedProduct.customLabel1 || '-'}</div>
                    <div className="text-gray-500">Custom label 2</div><div className="text-gray-800">{selectedProduct.customLabel2 || '-'}</div>
                    <div className="text-gray-500">Custom label 3</div><div className="text-gray-800">{selectedProduct.customLabel3 || '-'}</div>
                    <div className="text-gray-500">Custom label 4</div><div className="text-gray-800">{selectedProduct.customLabel4 || '-'}</div>
                  </div>
                  <div className="mt-5 flex items-center gap-2">
                    {selectedProduct.link ? (
                      <a href={selectedProduct.link} target="_blank" rel="noreferrer" className="inline-flex items-center px-3 py-2 text-xs font-medium rounded-lg bg-gray-900 text-white hover:bg-gray-800">View on site</a>
                    ) : null}
                    <button className="inline-flex items-center px-3 py-2 text-xs font-medium rounded-lg border hover:bg-gray-50" onClick={() => setSelectedProduct(null)}>Close</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
